import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Loader2, Users, Award, ChevronRight } from "lucide-react";
import { levelLabel } from "@/lib/levels";

export default function MyStudents() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/teacher/students");
        setStudents(data);
      } catch (e) {
        toast.error("Failed to load students");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(
    () =>
      students.filter((s) =>
        s.full_name.toLowerCase().includes(search.toLowerCase())
      ),
    [students, search]
  );

  return (
    <div className="max-w-5xl" data-testid="my-students-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-foreground">
        My Students
      </h1>
      <p className="mt-1 text-muted-foreground">
        Students assigned to you. Tap a student to view their profile.
      </p>

      <div className="relative mt-6 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          data-testid="my-students-search"
          className="h-11 pl-9"
          placeholder="Search by name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="mt-10 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="mt-6 bg-card border border-border rounded-lg p-12 text-center text-muted-foreground" data-testid="my-students-empty">
          <Users className="h-8 w-8 mx-auto mb-3 opacity-40" />
          {students.length === 0
            ? "No students assigned to you yet."
            : "No matches."}
        </div>
      ) : (
        <div className="mt-5 grid sm:grid-cols-2 gap-3">
          {filtered.map((s) => (
            <Link
              key={s.id}
              to={`/teacher/students/${s.id}`}
              data-testid={`student-card-${s.id}`}
              className="group bg-card border border-border rounded-lg p-5 transition-all duration-200 hover:-translate-y-[2px] hover:shadow-sm flex items-center justify-between"
            >
              <div>
                <p className="font-heading font-medium text-foreground">{s.full_name}</p>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {levelLabel(s.level) || "Level not set"}
                </p>
                <div className="flex items-center gap-2 mt-3">
                  <Badge variant="secondary" className="font-normal gap-1">
                    <Award className="h-3 w-3" /> {s.total_points} pts
                  </Badge>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
